import { memo } from "react";

import { Icon } from "@react/components/Icon";
import { Text } from "@react/components/Text";
import { type ThemeMode } from "@root/tablescape/state";
import { tw } from "classnames";

const styles = {
  container: tw("flex", "flex-row", "items-center", "justify-center"),
  baseButton: tw(
    "w-10",
    "h-10",
    "flex",
    "flex-row",
    "items-center",
    "justify-center"
  ),
  arrowButton: tw("text-primary", "cursor-pointer"),
  arrowButtonDisabled: tw("text-interactive-disabled-text"),
  pageButton: tw("border-solid", "border-0", "border-b-2"),
  pageButtonNotSelected: tw("border-transparent"),
  pageButtonSelected: tw("border-special"),
  text: tw("cursor-pointer", "text-primary"),
  selectedText: tw("text-special"),
  prevIcon: tw("w-4", "h-4", "rotate-180"),
  nextIcon: tw("w-4", "h-4"),
};

type Props = {
  goToPage: (page: number) => void;
  lastPage: number;
  pageButtonGroups: number[][];
  selectedPage: number;
  theme: ThemeMode;
};

const PaginationViewBase = ({
  goToPage,
  lastPage,
  pageButtonGroups,
  selectedPage,
  theme,
}: Props) => {
  return (
    <div className={styles.container} data-theme={theme}>
      <div
        className={tw(
          styles.baseButton,
          selectedPage === 1 ? styles.arrowButtonDisabled : styles.arrowButton
        )}
        onClick={() => goToPage(selectedPage - 1)}
      >
        <Icon className={styles.prevIcon} name="caret" />
      </div>
      {pageButtonGroups.map((buttonGroup: number[], index) => {
        const buttons = buttonGroup.map((page: number) => (
          <div
            className={tw({
              [styles.baseButton]: true,
              [styles.pageButton]: true,
              [styles.pageButtonSelected]: page === selectedPage,
              [styles.pageButtonNotSelected]: page !== selectedPage,
            })}
            key={`page-${page}`}
            onClick={() => goToPage(page)}
          >
            <Text
              className={
                page === selectedPage ? styles.selectedText : styles.text
              }
            >
              {page}
            </Text>
          </div>
        ));

        return (
          <div className={styles.container} key={`page-button-group-${index}`}>
            {buttons}
            {index !== pageButtonGroups.length - 1 && (
              <div className={styles.baseButton}>
                <Text>...</Text>
              </div>
            )}
          </div>
        );
      })}
      <div
        className={tw(
          styles.baseButton,
          selectedPage === lastPage
            ? styles.arrowButtonDisabled
            : styles.arrowButton
        )}
        onClick={() => goToPage(selectedPage + 1)}
      >
        <Icon className={styles.nextIcon} name="caret" />
      </div>
    </div>
  );
};

export const PaginationView = memo(PaginationViewBase);
